import { default as getObjValue } from 'lodash/get';

import { useState } from 'react';
import { errorHandler } from 'helper/errorHandler';

import Papa from 'papaparse';

/**
 * This callback type is called `requestCallback` and is displayed as a global symbol.
 *
 * @callback formatValue
 * @param {string|number} value - The value of key in T
 * @param {T} row - Object value of T

 * Columns type definition
 * @typedef {Object} Columns
 * @property {string} key - key in T
 * @property {string} name - Name of each columns in csv
 * @property {boolean} preventExport - The flag use for hide column
 * @property {formatValue} formatValue - Callback for pre-format data before export

 * PropTypes definition
 * @param {Object} props - The props of useExportData
 * @param {Columns[]} props.columns - The config format data
 * @param {T[]} props.rows - The list data for export
 * @param {string} props.filename - File name
 */

export default function useExportData({
  rows,
  columns,
  filename = 'download',
}) {
  const [isProcessing, setIsProcessing] = useState(false);

  const downLoadFilename = `${filename}.csv`;

  function downloadByUrl(url) {
    const tempLink = document.createElement('a');
    tempLink.href = url;
    tempLink.setAttribute('download', downLoadFilename);
    tempLink.click();
  }

  function convertData(rows, columns) {
    if (!columns?.length) {
      return {
        fields: [],
        data: [],
      };
    }

    const fields = [];
    const filterColumns = [];

    columns.forEach(({ name, preventExport, ...rest }) => {
      if (!preventExport) {
        fields.push(name);
        filterColumns.push(rest);
      }
    });

    return {
      fields,
      data: rows.map((row, index) => {
        return filterColumns.map(({ key, formatValue }) => {
          return typeof formatValue === 'function'
            ? formatValue(getObjValue(row, key), row)
            : getObjValue(row, key);
        });
      }),
    };
  }

  async function handleExport(rowsData) {
    try {
      setIsProcessing(true);

      // const t0 = performance.now()
      const { data, fields } = convertData(rowsData ?? rows, columns);
      // const t1 = performance.now()

      // console.log(`convertData: ${t1 - t0} milliseconds.`)

      const csv = Papa.unparse({
        data,
        fields,
      });

      // const t2 = performance.now()

      // console.log(`Papa.unparse: ${t2 - t1} milliseconds.`)

      const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      let csvURL = null;
      if (navigator.msSaveBlob) {
        csvURL = navigator.msSaveBlob(csvData, downLoadFilename);
      } else {
        csvURL = window.URL.createObjectURL(csvData);
      }

      // const t3 = performance.now()
      // console.log(`createObjectURL: ${t3 - t2} milliseconds.`)
      downloadByUrl(csvURL);

      // const t4 = performance.now()
      // console.log(`download: ${t4 - t3} milliseconds.`)
    } catch (error) {
      const { message } = errorHandler(error);
      console.log('message', message);
    } finally {
      setIsProcessing(false);
    }
  }

  return {
    handleExport,
    isProcessing,
  };
}
